import { useState } from 'react';
import {
  Banknote,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FolderKanban,
  Link2,
  MessageSquare,
  Plus,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TagBadge } from '@/components/shared/TagBadge';
import { AutoAssignedBadge } from '@/components/shared/AutoAssignedBadge';
import { CorrectionBadge } from '@/components/shared/CorrectionBadge';
import { CopyValueButton } from '@/components/shared/CopyValueButton';
import { skipRowNav, isRowNavHandled } from '@/components/shared/rowNav';
import { SelectionSummary } from '@/components/shared/SelectionSummary';
import { SettlementBadge } from './SettlementBadge';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';
import type { InvoiceListItem, Tag as TagType } from '@ip/shared-types';

interface SalesInvoiceTableProps {
  invoices: InvoiceListItem[];
  tags: TagType[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onRowClick: (invoiceId: string) => void;
  onTagChange: (invoiceId: string, currentTags: string[], tagId: string, checked: boolean) => void;
  /** "Zarejestruj wpłatę" row action (Task A3). */
  onRegisterPayment: (invoice: InvoiceListItem) => void;
  /** Single-row "Przypisz do projektu" action — hidden when the user has no
      access to the project list (canAssignProject false). */
  canAssignProject: boolean;
  onAssignProject: (invoice: InvoiceListItem) => void;
  /** Row selection for bulk actions — selection may span pages (by id). */
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  /** Selected rows with full data (cross-page cache) for the footer sum. */
  selectedItems: InvoiceListItem[];
}

function formatDate(date: string | null): string {
  return date ? new Date(date).toLocaleDateString('pl-PL') : '-';
}

/**
 * Right-aligned money cell: the amount in the invoice's original currency
 * (with the currency code shown, muted) and — for non-PLN invoices — a
 * muted second line with the PLN equivalent from the NBP conversion. Falls
 * back to a "no rate" hint when the invoice never got a converted amount
 * (e.g. conversion failed at ingest time).
 */
function MoneyCell({
  amount,
  plnAmount,
  currency,
  bold,
}: {
  amount: number | null;
  plnAmount: number | null;
  currency: string | null;
  bold?: boolean;
}) {
  if (amount === null) return <span>-</span>;
  const isForeign = Boolean(currency) && currency !== 'PLN';
  return (
    <div className="flex flex-col items-end leading-tight">
      <span className={bold ? 'font-semibold' : undefined}>
        {formatPLN(amount)}
        {isForeign && (
          <span className="text-muted-foreground ml-1 text-xs font-normal">{currency}</span>
        )}
      </span>
      {isForeign && (
        <span className="text-muted-foreground text-[11px] font-normal normal-case">
          {plnAmount !== null ? t('fx.approxPln', { amount: formatPLN(plnAmount) }) : t('fx.noRate')}
        </span>
      )}
    </div>
  );
}

const COLUMN_COUNT = 13;

export function SalesInvoiceTable({
  invoices,
  tags,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  onTagChange,
  onRegisterPayment,
  canAssignProject,
  onAssignProject,
  selectedIds,
  onSelectionChange,
  selectedItems,
}: SalesInvoiceTableProps) {
  const getTagById = (id: string) => tags.find((tag) => tag.id === id);
  // Only ONE row's tag popover may be open at a time — opening another row's
  // closes the previous one. Kept here (not as per-row local state) so the
  // "one open" rule is enforced across rows.
  const [openTagRowId, setOpenTagRowId] = useState<string | null>(null);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  // Page-scoped select-all (mirrors the cost list's header checkbox):
  // toggling only adds/removes the CURRENT page's ids, never other pages'.
  const pageIds = invoices.map((inv) => inv.id);
  const selectedOnPage = pageIds.filter((id) => selectedIds.includes(id));
  const allPageSelected = pageIds.length > 0 && selectedOnPage.length === pageIds.length;
  const somePageSelected = selectedOnPage.length > 0 && !allPageSelected;

  const toggleAllPage = (checked: boolean) => {
    onSelectionChange(
      checked
        ? [...new Set([...selectedIds, ...pageIds])]
        : selectedIds.filter((id) => !pageIds.includes(id)),
    );
  };

  const toggleRow = (invoiceId: string, checked: boolean) => {
    onSelectionChange(
      checked ? [...selectedIds, invoiceId] : selectedIds.filter((id) => id !== invoiceId),
    );
  };

  return (
    <div className="bg-card rounded-xl border">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-10">
                <Checkbox
                  checked={allPageSelected || (somePageSelected && 'indeterminate')}
                  onCheckedChange={(checked) => toggleAllPage(checked === true)}
                  aria-label="Select all"
                />
              </TableHead>
              <TableHead className="text-muted-foreground">{t('sales.columns.customer')}</TableHead>
              <TableHead className="text-muted-foreground">{t('sales.columns.invoiceNumber')}</TableHead>
              {/* Accounting-notes indicator — same pattern as the cost list
                  (InvoiceTable 'notes' column, size 56). */}
              <TableHead className="text-muted-foreground w-10">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <MessageSquare className="size-3.5" />
                  </TooltipTrigger>
                  <TooltipContent>{t('invoices.notes.title')}</TooltipContent>
                </Tooltip>
              </TableHead>
              <TableHead className="text-muted-foreground">{t('sales.columns.saleDate')}</TableHead>
              <TableHead className="text-muted-foreground text-right">{t('sales.columns.net')}</TableHead>
              <TableHead className="text-muted-foreground text-right">{t('sales.columns.vat')}</TableHead>
              <TableHead className="text-muted-foreground text-right">{t('sales.columns.gross')}</TableHead>
              <TableHead className="text-muted-foreground">{t('sales.columns.ksefNumber')}</TableHead>
              <TableHead className="text-muted-foreground">{t('sales.columns.settlement')}</TableHead>
              <TableHead className="text-muted-foreground">{t('sales.columns.project')}</TableHead>
              <TableHead className="text-muted-foreground">{t('sales.columns.tags')}</TableHead>
              <TableHead className="text-muted-foreground">{t('sales.columns.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="h-40 text-center">
                  <p className="font-medium">{t('sales.noInvoices')}</p>
                  <p className="text-muted-foreground text-sm">{t('sales.noInvoicesDescription')}</p>
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((inv) => (
                <TableRow
                  key={inv.id}
                  className="group cursor-pointer"
                  data-state={selectedIds.includes(inv.id) ? 'selected' : undefined}
                  onClick={(e) => {
                    if (isRowNavHandled(e)) return;
                    onRowClick(inv.id);
                  }}
                >
                  <TableCell className="py-2" onClick={skipRowNav}>
                    <Checkbox
                      checked={selectedIds.includes(inv.id)}
                      onCheckedChange={(checked) => toggleRow(inv.id, checked === true)}
                      aria-label="Select row"
                    />
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-1">
                      <p className="max-w-56 truncate text-sm font-medium" title={inv.customerName || inv.customerNip || '-'}>
                        {inv.customerName || '-'}
                      </p>
                      {inv.customerName && <CopyValueButton value={inv.customerName} />}
                    </div>
                    {inv.customerNip && (
                      <p className="text-muted-foreground font-mono text-xs">NIP: {inv.customerNip}</p>
                    )}
                  </TableCell>
                  <TableCell className="py-2">
                    <span className="flex items-center gap-1.5">
                      <span className="font-mono text-sm">{inv.invoiceNumber || '-'}</span>
                      <CorrectionBadge rodzajFaktury={inv.rodzajFaktury} />
                      {inv.invoiceNumber && <CopyValueButton value={inv.invoiceNumber} />}
                    </span>
                  </TableCell>
                  <TableCell className="py-2">
                    {(inv.notesCount ?? 0) > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1 text-sky-600 dark:text-sky-400">
                            <MessageSquare className="size-4" />
                            <span className="text-xs tabular-nums">{inv.notesCount}</span>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {t('invoices.notes.count', { count: inv.notesCount ?? 0 })}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell className="py-2 tabular-nums">
                    {formatDate(inv.saleDate ?? inv.issueDate)}
                  </TableCell>
                  <TableCell className="py-2 text-right font-mono text-sm tabular-nums">
                    <MoneyCell amount={inv.netAmount} plnAmount={inv.netPln} currency={inv.currency} />
                  </TableCell>
                  <TableCell className="py-2 text-right font-mono text-sm tabular-nums">
                    <MoneyCell amount={inv.taxAmount} plnAmount={inv.taxPln} currency={inv.currency} />
                  </TableCell>
                  <TableCell className="py-2 text-right font-mono text-sm tabular-nums">
                    <div className="flex items-center justify-end gap-1">
                      <MoneyCell amount={inv.amount} plnAmount={inv.grossPln} currency={inv.currency} bold />
                      {inv.amount !== null && (
                        <CopyValueButton value={formatPLN(inv.amount)} />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-2">
                    {inv.ksefNumber ? (
                      <span className="inline-flex items-center gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-muted-foreground inline-block max-w-32 truncate align-bottom font-mono text-xs">
                              {inv.ksefNumber}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{inv.ksefNumber}</TooltipContent>
                        </Tooltip>
                        <CopyValueButton value={inv.ksefNumber} />
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2">
                    <span className="flex items-center gap-1.5">
                      {inv.settlementStatus ? (
                        <SettlementBadge
                          status={inv.settlementStatus}
                          isOverdue={inv.isOverdue}
                          daysOverdue={inv.daysOverdue}
                          outstanding={inv.outstanding}
                          currency={inv.currency}
                        />
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                      {inv.combinedPaymentId && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link2 className="size-3.5 text-sky-600 dark:text-sky-400" />
                          </TooltipTrigger>
                          <TooltipContent>{t('invoices.combinedPayment.badgeTooltip')}</TooltipContent>
                        </Tooltip>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="py-2">
                    {/* Task B3: whole-invoice project; the "częściowo" badge
                        marks invoices whose lines carry their own assignments
                        (lines count to their projects, the residual to the
                        invoice's project). */}
                    {inv.projectName || inv.hasLineProjects ? (
                      <span className="flex items-center gap-1">
                        {inv.projectName && (
                          <span className="block max-w-28 truncate text-xs" title={inv.projectName}>
                            {inv.projectName}
                          </span>
                        )}
                        {/* Provenance: rule-made and correction-inherited assignments get an "auto" hint. */}
                        {inv.projectName &&
                          (inv.projectAssignedBy === 'rule' || inv.projectAssignedBy === 'correction') && (
                            <AutoAssignedBadge variant={inv.projectAssignedBy} />
                          )}
                        {inv.hasLineProjects && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-muted-foreground text-xs italic">
                                {t('projects.partialBadge')}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{t('projects.partialTooltip')}</TooltipContent>
                          </Tooltip>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2" onClick={skipRowNav}>
                    <Popover
                      open={openTagRowId === inv.id}
                      onOpenChange={(open) => setOpenTagRowId(open ? inv.id : null)}
                    >
                      <PopoverTrigger asChild>
                        <div className="flex min-h-[22px] cursor-pointer flex-wrap items-center gap-1">
                          {inv.tags.map((id) => {
                            const tag = getTagById(id);
                            return tag ? <TagBadge key={id} name={tag.name} color={tag.color} /> : null;
                          })}
                          {inv.tags.length === 0 && (
                            <span className="text-muted-foreground inline-flex items-center gap-0.5 rounded-md border border-dashed px-2 py-0.5 text-xs">
                              <Plus className="size-3" /> {t('common.add')}
                            </span>
                          )}
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 space-y-2" align="start">
                        {tags.map((tag) => (
                          <Label key={tag.id} className="flex items-center gap-2 py-0.5 font-normal">
                            <Checkbox
                              checked={inv.tags.includes(tag.id)}
                              onCheckedChange={(checked) =>
                                onTagChange(inv.id, inv.tags, tag.id, checked === true)
                              }
                            />
                            <TagBadge name={tag.name} color={tag.color} />
                          </Label>
                        ))}
                        {tags.length === 0 && (
                          <p className="text-muted-foreground text-sm">{t('invoices.tagsEditor.noTags')}</p>
                        )}
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                  <TableCell className="py-2" onClick={skipRowNav}>
                    <div className="flex items-center gap-0.5">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7"
                            aria-label={t('sales.payments.register')}
                            onClick={() => onRegisterPayment(inv)}
                          >
                            <Banknote className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('sales.payments.register')}</TooltipContent>
                      </Tooltip>
                      {/* Whole-invoice project stays assignable even when
                          lines carry their own projects — it receives the
                          residual. */}
                      {canAssignProject && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-7"
                              aria-label={t('invoices.bulk.assignToProject')}
                              onClick={() => onAssignProject(inv)}
                            >
                              <FolderKanban className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-64">
                            {t('invoices.bulk.assignToProject')}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2">
        <p className="text-muted-foreground text-sm">
          {t('pagination.showing', { from, to, total })}
        </p>
        <div className="flex items-center gap-3">
          <SelectionSummary items={selectedItems} />
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
            <SelectTrigger size="sm" className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="size-8" disabled={page <= 1} onClick={() => onPageChange(1)}>
              <ChevronsLeft className="size-4" />
            </Button>
            <Button variant="outline" size="icon" className="size-8" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              <ChevronLeft className="size-4" />
            </Button>
            <span className="px-2 text-sm tabular-nums">
              {page} / {totalPages}
            </span>
            <Button variant="outline" size="icon" className="size-8" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
              <ChevronRight className="size-4" />
            </Button>
            <Button variant="outline" size="icon" className="size-8" disabled={page >= totalPages} onClick={() => onPageChange(totalPages)}>
              <ChevronsRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
